import type * as THREE from "three";

/**
 * Light station contract for the Interactive Electrical Workshop.
 *
 * Each station owns its own root, local progress, and {@link reset}. Stations
 * do not gate each other — learners walk freely between benches.
 *
 * Extension points:
 * - Environment: attach second-bench meshes under `root` (or replace placeholder).
 * - Hands: register grabables against station-local objects in `register` / ctor.
 * - Electrical sim (`src/electrical/`): subscribe from station update/reset hooks.
 */
export interface WorkshopStation {
  /** Stable id used by {@link Workshop} (e.g. `"outlet-assembly"`). */
  readonly id: string;

  /** Scene subgraph for this station (props, HUD, reset plate, etc.). */
  readonly root: THREE.Object3D;

  /**
   * Optional world-position source for proximity (defaults to {@link root}).
   * Use a bench / stand marker when `root` sits at the room origin.
   */
  readonly activationAnchor?: THREE.Object3D;

  /**
   * Horizontal distance (meters) from player to
   * {@link activationAnchor} / {@link root} for proximity.
   * Default applied by Workshop when omitted: 2.5 m.
   */
  readonly activationRadius?: number;

  /** True while the player is within {@link activationRadius}. */
  active: boolean;

  /** Optional intro panel / coaching affordance. */
  showIntro?(): void;
  hideIntro?(): void;

  /**
   * Station-local reset only. Must not wipe other stations' progress or
   * teleport the player. May clear sticky input for this station's grabs.
   */
  reset(): void;

  /**
   * Per-frame station logic. `playerWorldPos` is the locomotion-root position
   * (approx. floor origin under the headset / desktop camera parent).
   */
  update(dt: number, playerWorldPos: THREE.Vector3): void;

  /**
   * Session start/end: drop held objects / clear phantom ownership for this
   * station's grab systems without undoing locked placements or step progress.
   */
  releaseSessionHolds(): void;
}

/** Default proximity radius when a station does not set its own. */
export const DEFAULT_STATION_ACTIVATION_RADIUS = 2.5;
